import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';

import {
  SOFTLINE_API_CASH_REGISTER_CART_PAYMENT,
  SOFTLINE_API_CASH_REGISTER_CART_PAYMENT_CHECK,
  SOFTLINE_API_CASH_REGISTER_CART_PAYMENT_SCAN,
  SOFTLINE_API_CASH_REGISTER_OPEN_REGISTER,
  SOFTLINE_API_CASH_REGISTER_PAYMENT_METHODS,
  SOFTLINE_API_CASH_REGISTER_PRINT_LAST_RECEIPT,
} from '../main.api';
import { Payment } from '@softapps/wws/pos-core';
import { Scan } from '@softline/application';
import { PaymentMethod } from '../types/payment-method';

export interface IPaymentRepository {
  loadPayment(cartId: number): Promise<Payment>;
  loadPaymentMethods(cartId: number): Promise<PaymentMethod[]>;
  addPaymentEntry(
    cartId: number,
    body: { paymentMethod: PaymentMethod; amount: number }
  ): Promise<Payment>;
  paymentCheck(cartId: number): Promise<any>;

  openCashregister(): Promise<void>;
}

@Injectable()
export class PaymentRepository implements IPaymentRepository {
  readonly #dataAccess = inject(ApiDataAccess);

  async loadPayment(cartId: number): Promise<Payment> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_CASH_REGISTER_CART_PAYMENT,
      pathParams: {
        cartId: cartId,
      },
    });
  }

  async printLastTerminalDocument(): Promise<void> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_PRINT_LAST_RECEIPT,
      },
      {}
    );
  }

  async loadPaymentMethods(cartId: number): Promise<PaymentMethod[]> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_CASH_REGISTER_PAYMENT_METHODS,
      pathParams: {
        cartId: cartId,
      },
    });
  }

  async addPaymentEntry(
    cartId: number,
    body: { paymentMethod: PaymentMethod; amount?: number; cashbackAmount?: number }
  ): Promise<Payment> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_PAYMENT,
        pathParams: {
          cartId: cartId,
        },
      },
      body
    );
  }

  async paymentCheck(cartId: number): Promise<any> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_PAYMENT_CHECK,
        pathParams: {
          cartId: cartId,
        },
      },
      {}
    );
  }

  async removePaymentEntry(cartId: number, paymentEntryId: number): Promise<Payment> {
    return this.#dataAccess.delete({
      path: `${SOFTLINE_API_CASH_REGISTER_CART_PAYMENT}/${paymentEntryId}`,
      pathParams: {
        cartId: cartId,
      },
    });
  }

  async openCashregister(): Promise<void> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_OPEN_REGISTER,
      },
      {}
    );
  }

  async scan(
    cartId: number,
    labelType: Scan['labelType'],
    data: Scan['data']
  ): Promise<Payment> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_PAYMENT_SCAN,
        pathParams: {
          cartId,
          labelType,
          code: data,
        },
      },
      {}
    );
  }
}
